"use client";

import { useState } from "react";
import { useQuery, useAction } from "convex/react";
import { Link } from "react-router-dom";
import { api } from "../../../../convex/_generated/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageTitle } from "@/lib/usePageTitle";
import {
  PRICING_PLANS,
  type SubscriptionTier,
  type SubscriptionStatus,
} from "@/lib/billing/plans";

function tierLabel(tier: SubscriptionTier) {
  switch (tier) {
    case "starter": return "Founder Starter";
    case "plus": return "Founder Plus";
    case "pro": return "Founder Pro";
    case "enterprise": return "Enterprise";
    default: return "Free";
  }
}

function formatDate(ts: number | null | undefined) {
  if (!ts) return null;
  return new Date(ts).toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function StatusBadge({
  status,
  cancelAtPeriodEnd,
}: {
  status: SubscriptionStatus;
  cancelAtPeriodEnd?: boolean | null;
}) {
  if (cancelAtPeriodEnd) {
    return (
      <Badge variant="secondary" className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
        Cancellation Scheduled
      </Badge>
    );
  }
  if (status === "active") return <Badge variant="success">Active</Badge>;
  if (status === "trialing") {
    return <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">Trial</Badge>;
  }
  if (status === "past_due") return <Badge variant="destructive">Past Due</Badge>;
  if (status === "canceled") return <Badge variant="secondary">Canceled</Badge>;
  return null;
}

function PlanFeatures({ tier }: { tier: SubscriptionTier }) {
  const plan = PRICING_PLANS.find((p) => p.id === tier);
  if (!plan) return null;
  return (
    <ul className="mt-3 space-y-1.5">
      {plan.features.map((f) => (
        <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
          <svg className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
          {f}
        </li>
      ))}
    </ul>
  );
}

export default function AccountBillingPage() {
  usePageTitle("Billing");

  const summary = useQuery(api.billing.getMySummary);
  const openPolarPortal = useAction(api.actions.openCustomerPortal);
  const openStripePortal = useAction(api.actions.openStripeCustomerPortal);

  const [portalAction, setPortalAction] = useState<"polar" | "stripe" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isLoading = summary === undefined;
  const tier = (summary?.tier ?? "free") as SubscriptionTier;
  const status = (summary?.status ?? "none") as SubscriptionStatus;
  const hasSubscription = !!summary?.subscriptionId;
  const isCancelScheduled = summary?.cancelAtPeriodEnd === true;
  const billingDate = formatDate(summary?.currentPeriodEnd);
  const accessEndsDate = formatDate(summary?.endsAt ?? summary?.currentPeriodEnd);
  const hasPolarCustomer = !!summary?.customerId;
  const hasStripeCustomer = !!summary?.stripeCustomerId;

  async function handleOpenPolarPortal() {
    setError(null);
    setPortalAction("polar");
    try {
      const result = await openPolarPortal({});
      if (!result.ok) throw new Error(result.error?.message ?? "Failed to open portal.");
      if (result.data?.url) window.location.href = result.data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to open customer portal.");
    } finally {
      setPortalAction(null);
    }
  }

  async function handleOpenStripePortal() {
    setError(null);
    setPortalAction("stripe");
    try {
      const result = await openStripePortal({});
      if (!result.ok) throw new Error(result.error?.message ?? "Failed to open Stripe portal.");
      if (result.data?.url) window.location.href = result.data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to open Stripe portal.");
    } finally {
      setPortalAction(null);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Billing</h1>
          <p className="text-sm text-muted-foreground">
            View your subscription and manage payment details
          </p>
        </div>
        <Link to="/dashboard/settings/billing" className="text-sm text-primary hover:underline">
          Full Billing Settings
        </Link>
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Current plan card */}
      {isLoading ? (
        <Card>
          <CardHeader>
            <CardTitle>Current Plan</CardTitle>
            <CardDescription>Loading your subscription status...</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-4 w-40" />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle>Current Plan</CardTitle>
              {hasSubscription && (
                <StatusBadge status={status} cancelAtPeriodEnd={summary?.cancelAtPeriodEnd} />
              )}
            </div>
            <CardDescription>
              {hasSubscription
                ? isCancelScheduled
                  ? "Your subscription is scheduled to cancel at the end of the current billing period."
                  : "You have an active subscription."
                : "You are on the free plan. Upgrade to unlock server management features."}
            </CardDescription>
          </CardHeader>

          {hasSubscription && (
            <CardContent className="space-y-5">
              {/* Plan details grid */}
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-md border border-border bg-foreground/5 p-4">
                  <div className="text-xs text-muted-foreground">Plan</div>
                  <div className="mt-1 text-sm font-semibold">{tierLabel(tier)}</div>
                </div>

                <div className="rounded-md border border-border bg-foreground/5 p-4">
                  <div className="text-xs text-muted-foreground">
                    {isCancelScheduled ? "Access ends" : "Next billing date"}
                  </div>
                  <div className="mt-1 text-sm font-semibold">
                    {(isCancelScheduled ? accessEndsDate : billingDate) ?? "\u2014"}
                  </div>
                  {isCancelScheduled && (
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      Then downgraded to free
                    </div>
                  )}
                </div>

                <div className="rounded-md border border-border bg-foreground/5 p-4">
                  <div className="text-xs text-muted-foreground">Status</div>
                  <div className="mt-1 text-sm font-semibold capitalize">{status}</div>
                </div>
              </div>

              {/* Features included */}
              {tier !== "free" && (
                <div>
                  <p className="text-sm font-medium">Included with your plan</p>
                  <PlanFeatures tier={tier} />
                </div>
              )}
            </CardContent>
          )}

          {!hasSubscription && (
            <CardContent>
              <Link to="/pricing">
                <Button>View Plans</Button>
              </Link>
            </CardContent>
          )}
        </Card>
      )}

      {/* Manage Billing card */}
      <Card>
        <CardHeader>
          <CardTitle>Manage Billing</CardTitle>
          <CardDescription>
            Update payment methods, view invoices, and manage your subscription through the customer portal.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <Skeleton className="h-10 w-48" />
          ) : (
            <>
              <div className="flex flex-wrap gap-3">
                {/* Stripe customer portal button */}
                {hasStripeCustomer && (
                  <Button
                    onClick={handleOpenStripePortal}
                    disabled={portalAction !== null}
                  >
                    {portalAction === "stripe" ? "Opening..." : "Manage Billing"}
                  </Button>
                )}

                {/* Polar customer portal button */}
                {hasPolarCustomer && (
                  <Button
                    variant={hasStripeCustomer ? "outline" : "default"}
                    onClick={handleOpenPolarPortal}
                    disabled={portalAction !== null}
                  >
                    {portalAction === "polar" ? "Opening..." : (hasStripeCustomer ? "Polar Portal" : "Manage Billing")}
                  </Button>
                )}

                {/* No billing customer at all */}
                {!hasStripeCustomer && !hasPolarCustomer && (
                  <div>
                    <p className="text-sm text-muted-foreground">
                      No billing account found. Subscribe to a plan to access billing management.
                    </p>
                    <Link to="/pricing" className="mt-2 inline-block">
                      <Button variant="outline">View Plans</Button>
                    </Link>
                  </div>
                )}
              </div>

              {(hasStripeCustomer || hasPolarCustomer) && (
                <p className="text-xs text-muted-foreground">
                  You'll be redirected to a secure portal where you can update your payment method, download invoices, and manage your subscription.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Quick links */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Links</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Link to="/account/subscription">
              <Button variant="outline" size="sm">Subscription Details</Button>
            </Link>
            <Link to="/dashboard/settings/billing">
              <Button variant="outline" size="sm">Change Plan</Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" size="sm">View Plans</Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
